import { ComplianceLifecycle, CreditRiskLifecycle } from "../../../api/Api";
import { HubSyncValues, SyncEntityType } from "./utils";
import { Progress } from "../departmentProgress/utils";

/**
 * Which lifecycle slice stores each syncable field. This is the canonical statement of that
 * mapping:
 *
 *   kycCheck        complianceLifecycle   (and mirrors into sanctionScreening, see below)
 *   creditCheck     creditRiskLifecycle   (the compliance dialog only shows it, disabled)
 *   creditAmount    creditRiskLifecycle   (same)
 *   creditCurrency  creditRiskLifecycle   (same)
 *
 * Before the Risk / Credit Risk split the credit check sat on riskLifecycle and the amount and
 * currency on complianceLifecycle. The backend rebuilds old documents into this shape on read, so
 * nothing here needs to know about the old one.
 *
 * Counterparties and brokers share this: both store `CreditRiskLifecycle`.
 */
export type SyncPatchTarget = {
  complianceLifecycle?: ComplianceLifecycle;
  creditRiskLifecycle?: CreditRiskLifecycle;
  /** Market Access decides whether a KYC check on MFT is needed; Compliance answers it. */
  marketAccessLifecycle?: { kycCheckOnMft?: string };
  progress?: { creditRisk?: string; compliance?: string };
  creditCatalystSyncedAt?: string | null;
};

/** The values Credit Risk owns among the syncable fields. */
const CREDIT_RISK_OWNED_FIELDS: (keyof HubSyncValues)[] = [
  "creditCheck",
  "creditAmount",
  "creditCurrency"
];

/** True when an accepted patch fills in anything Credit Risk owns, so its progress has to move. */
export const patchTouchesCreditRiskData = (patch: Partial<HubSyncValues>) =>
  CREDIT_RISK_OWNED_FIELDS.some((field) => patch[field] !== undefined);

/**
 * Where the Credit Risk lifecycle's progress lands once a sync has filled in its data: completed.
 *
 * Before the split this was "completed when the mandate is settled, in progress otherwise", because
 * the mandate shared the lifecycle with the credit check. The mandate now lives on Risk alone, so a
 * synced credit check is the whole of Credit Risk's work and nothing is left to wait for. As before,
 * the credit check's value is not looked at: a synced rejection completes it too.
 *
 * Note this moves `progress.creditRisk` only and deliberately leaves `creditRiskLifecycle.complete`
 * alone: the backend worker emails a lifecycle's assignees when that flag flips false to true, and a
 * sweep across the whole book would turn into one email per entity.
 */
export const syncedCreditRiskProgress = () => Progress.Completed;

/**
 * The values Compliance owns among the syncable fields. Only the KYC check: sanctionScreening is
 * not synced in its own right, it is mirrored off the KYC check below.
 */
const COMPLIANCE_OWNED_FIELDS: (keyof HubSyncValues)[] = ["kycCheck"];

/** True when an accepted patch fills in anything Compliance owns, so their progress has to move. */
export const patchTouchesComplianceData = (patch: Partial<HubSyncValues>) =>
  COMPLIANCE_OWNED_FIELDS.some((field) => patch[field] !== undefined);

/**
 * Where the Compliance lifecycle's progress lands once a sync has filled in its data. Completed
 * when nothing Compliance owes is outstanding:
 *
 *   KYC check approved, and
 *   either Market Access did not ask for a KYC check on MFT, or it did and that one is approved too,
 *   and (brokers only) REMIT reporting has been answered, needed or not needed.
 *
 * Anything short of that is in progress: a sync that lands a KYC check has started the work even
 * when it did not finish it.
 *
 * REMIT reporting is a broker-only question, which is why this takes the entity type. It is not
 * synced from the warehouse, so like the MFT check it is read off the stored entity.
 *
 * The KYC check is read from the patch when the patch carries one, since that is the value about to
 * be written; everything else comes from the stored entity untouched.
 *
 * Same restraint as syncedCreditRiskProgress: this moves `progress.compliance` only and never
 * `complianceLifecycle.complete`, because the backend worker emails a lifecycle's assignees when
 * that flag flips false to true and a whole-book sweep would become one email per entity.
 */
export const syncedComplianceProgress = (
  entity: SyncPatchTarget,
  patch: Partial<HubSyncValues>,
  entityType: SyncEntityType
) => {
  const kycCheck = patch.kycCheck ?? entity.complianceLifecycle?.kycCheck;

  const mftCheckSettled =
    entity.marketAccessLifecycle?.kycCheckOnMft !== "needed" ||
    entity.complianceLifecycle?.kycCheckMft === "approved";

  const remitSettled =
    entityType !== "broker" || !!entity.complianceLifecycle?.remitReporting;

  const settled = kycCheck === "approved" && mftCheckSettled && remitSettled;

  return settled ? Progress.Completed : Progress.InProgress;
};

/**
 * The KYC check dropdown writes sanctionScreening to the same value, so a synced KYC check has to
 * carry it too or the two silently drift apart. Shared by the dialogs, which stage a patch into
 * local form state rather than onto an entity, and by applySyncPatch below.
 */
export const mirroredSanctionScreening = (patch: Partial<HubSyncValues>) =>
  patch.kycCheck !== undefined ? { sanctionScreening: patch.kycCheck } : {};

/** The credit values a patch carries, ready to spread onto creditRiskLifecycle. */
export const creditRiskChanges = (
  patch: Partial<HubSyncValues>
): Partial<CreditRiskLifecycle> => ({
  ...(patch.creditCheck !== undefined
    ? { creditCheck: patch.creditCheck }
    : {}),
  ...(patch.creditAmount !== undefined
    ? { creditAmount: patch.creditAmount }
    : {}),
  ...(patch.creditCurrency !== undefined
    ? { creditCurrency: patch.creditCurrency }
    : {})
});

/**
 * Applies an accepted sync patch to a stored counterparty or broker, routing every field to the
 * slice that owns it. Returns a new entity; the input is not mutated.
 *
 * Used by the bulk warehouse sync page, which writes straight to the entity. The lifecycle dialogs
 * stage their patch in local form state instead (the user can still edit before saving), so they
 * do not call this, but the ownership rules above are the same ones their save paths follow.
 */
export const applySyncPatch = <T extends SyncPatchTarget>(
  entity: T,
  patch: Partial<HubSyncValues>,
  syncedAt: string,
  entityType: SyncEntityType
): T => {
  const complianceChanges: Partial<ComplianceLifecycle> = {
    ...(patch.kycCheck !== undefined ? { kycCheck: patch.kycCheck } : {}),
    ...mirroredSanctionScreening(patch)
  };

  const creditChanges = creditRiskChanges(patch);

  // Each lifecycle's progress moves only when the patch actually reached data that lifecycle owns,
  // so a credit-only sync never restates Compliance's progress and a KYC-only sync never restates
  // Credit Risk's. Built as one object because both land on the same `progress` slice.
  const progressChanges = {
    ...(patchTouchesCreditRiskData(patch)
      ? { creditRisk: syncedCreditRiskProgress() }
      : {}),
    ...(patchTouchesComplianceData(patch)
      ? { compliance: syncedComplianceProgress(entity, patch, entityType) }
      : {})
  };

  return {
    ...entity,
    creditCatalystSyncedAt: syncedAt,
    // Only touch a slice the patch actually reaches, so an untouched lifecycle is not rewritten
    // with an identical copy of itself on every apply.
    ...(Object.keys(complianceChanges).length
      ? {
          complianceLifecycle: {
            ...entity.complianceLifecycle,
            ...complianceChanges
          }
        }
      : {}),
    ...(Object.keys(creditChanges).length
      ? {
          creditRiskLifecycle: {
            ...entity.creditRiskLifecycle,
            ...creditChanges
          }
        }
      : {}),
    // Filling in a lifecycle's data moves that lifecycle's progress with it. See
    // syncedCreditRiskProgress and syncedComplianceProgress.
    ...(Object.keys(progressChanges).length
      ? { progress: { ...entity.progress, ...progressChanges } }
      : {})
  };
};
